// AI USAGE & PLAN QUOTAS (appsbooth/busy-intelligent-srv#19).
//
// busy-intelligent-srv counts every AI call per user and meters it against the plan's monthly
// token quota; busy-main-bff decides the plan (an active Pro is `pro`, everyone else `free`; `max`
// is configured there but not sold yet) and serves the signed-in user's month as
// `GET v1/intelligent/usage/me?period=YYYY-MM`:
//
//   { plan, period, resets_at, used_tokens, quota_tokens, remaining_tokens, cost_usd,
//     percent_used, by_feature: { ask: { tokens, cost_usd, requests } }, by_day: [{ date, tokens }],
//     plans: [{ plan, quota_tokens, cost_cap_usd, soft_limit }] }
//
// Everything the Usage settings page (views/settings/UsageSettings.vue) draws is shaped here, as
// plain functions, so it is testable without mounting the page. Over quota, a request may come
// back 429 `quota_exceeded` (only once the service enforces it) — `isQuotaExceeded` /
// `quotaExceededMessage` turn that into the sentence the interceptor shows.

/** The plans, cheapest first — the order the cards are drawn in. */
export const PLAN_ORDER = ['free', 'pro', 'max']

export const PLAN_LABELS = { free: 'Free', pro: 'Pro', max: 'Max' }

/** Placeholder quotas, used only when the service did not send its `plans` list. */
export const DEFAULT_PLAN_QUOTAS = { free: 200000, pro: 5000000, max: 25000000 }

/** At this share of the quota the page warns; at 100 it says the allowance is spent. */
export const WARN_PERCENT = 80

/** What each metered feature is called on the page. Unknown keys fall back to a tidied name. */
export const FEATURE_LABELS = {
  ask: 'Chat answers',
  plan: 'Mailbox search planning',
  draft: 'Reply drafts',
  parse: 'Email parser',
  follow_ups: 'Follow-up suggestions',
  possible_relations: 'Possible connections',
  email_matches: 'Ongoing matches',
}

export const normalizePlan = (plan) => {
  const key = String(plan ?? '').trim().toLowerCase()
  return PLAN_ORDER.includes(key) ? key : 'free'
}

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** `YYYY-MM` of a date in UTC — the service's month boundary. */
export const currentPeriod = (now = new Date()) =>
  `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`

/**
 * The BFF answer (bare, or wrapped in its `{ data }` envelope) as a complete usage object —
 * missing numbers are 0, the plan is one of the three, percent is recomputed when absent.
 */
export function normalizeUsage(raw) {
  const u = raw && typeof raw === 'object' && raw.data && typeof raw.data === 'object' ? raw.data : (raw || {})
  const plan = normalizePlan(u.plan)
  const quota = num(u.quota_tokens) || DEFAULT_PLAN_QUOTAS[plan]
  const used = num(u.used_tokens)
  const percent = u.percent_used == null ? (quota > 0 ? Math.round((used * 1000) / quota) / 10 : 0) : num(u.percent_used)
  return {
    plan,
    period: u.period || currentPeriod(),
    resetsAt: u.resets_at || null,
    usedTokens: used,
    quotaTokens: quota,
    remainingTokens: u.remaining_tokens == null ? Math.max(quota - used, 0) : num(u.remaining_tokens),
    costUsd: num(u.cost_usd),
    percentUsed: percent,
    softLimit: !!u.soft_limit,
    byFeature: u.by_feature && typeof u.by_feature === 'object' ? u.by_feature : {},
    byDay: Array.isArray(u.by_day) ? u.by_day : [],
    plans: Array.isArray(u.plans) ? u.plans : [],
  }
}

/** 950 → "950", 12_345 → "12.3k", 5_000_000 → "5M". */
export function formatTokens(n) {
  const v = num(n)
  const fmt = (x, unit) => `${Number(x.toFixed(x < 10 ? 1 : 0))}${unit}`
  if (Math.abs(v) >= 1e6) return fmt(v / 1e6, 'M')
  if (Math.abs(v) >= 1e3) return fmt(v / 1e3, 'k')
  return String(Math.round(v))
}

/** USD with cents, or "<$0.01" for the fractions a light month costs. */
export function formatCost(usd) {
  const v = num(usd)
  if (v > 0 && v < 0.01) return '<$0.01'
  return `$${v.toFixed(2)}`
}

/** "1 Nov" style date of the reset, or '' when unknown/unparseable. */
export function formatResetDate(iso, locale) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** 'ok' | 'warning' (≥ 80%) | 'exceeded' (≥ 100%). */
export function usageLevel(percent) {
  const p = num(percent)
  if (p >= 100) return 'exceeded'
  if (p >= WARN_PERCENT) return 'warning'
  return 'ok'
}

/** The progress bar's fill, clamped to 0–100. */
export const barPercent = (percent) => Math.min(100, Math.max(0, num(percent)))

/**
 * The banner over the page at 80% and 100%, or null below 80%.
 * @returns {{ level: 'warning'|'exceeded', title: string, detail: string, canUpgrade: boolean } | null}
 */
export function usageBanner(usage) {
  if (!usage) return null
  const level = usageLevel(usage.percentUsed)
  if (level === 'ok') return null
  const resets = formatResetDate(usage.resetsAt)
  const when = resets ? ` It resets on ${resets}.` : ''
  const canUpgrade = usage.plan === 'free'
  if (level === 'exceeded') {
    return {
      level,
      title: "You've used this month's AI allowance",
      detail: usage.softLimit
        ? `You're over your ${PLAN_LABELS[usage.plan]} plan's allowance.${when}`
        : `AI features may be paused until it resets.${when}${canUpgrade ? ' Upgrade to Pro for a larger allowance.' : ''}`,
      canUpgrade,
    }
  }
  return {
    level,
    title: `You've used ${Math.floor(num(usage.percentUsed))}% of this month's AI allowance`,
    detail: `${formatTokens(usage.remainingTokens)} tokens left.${when}${canUpgrade ? ' Upgrade to Pro for a larger allowance.' : ''}`,
    canUpgrade,
  }
}

const featureLabel = (key) =>
  FEATURE_LABELS[key] || String(key || 'Other').replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

/** The by-feature table: biggest spender first, each with its share of the month. */
export function featureRows(byFeature, usedTokens) {
  const total = num(usedTokens)
  return Object.entries(byFeature || {})
    .map(([key, v]) => ({
      key,
      label: featureLabel(key),
      tokens: num(v?.tokens),
      costUsd: num(v?.cost_usd),
      requests: num(v?.requests),
    }))
    .filter((r) => r.tokens > 0 || r.requests > 0)
    .sort((a, b) => b.tokens - a.tokens || a.label.localeCompare(b.label))
    .map((r) => ({ ...r, share: total > 0 ? Math.round((r.tokens * 100) / total) : 0 }))
}

/**
 * The daily chart's bars: one per day of the month, height as a share of the busiest day.
 * @returns {{ bars: Array<{ date, day, tokens, height, title }>, maxTokens: number }}
 */
export function dailyBars(byDay) {
  const days = (Array.isArray(byDay) ? byDay : []).filter((d) => d && d.date)
  const maxTokens = days.reduce((m, d) => Math.max(m, num(d.tokens)), 0)
  const bars = days.map((d) => {
    const tokens = num(d.tokens)
    return {
      date: d.date,
      day: Number(String(d.date).slice(8, 10)) || 0,
      tokens,
      height: maxTokens > 0 ? Math.round((tokens * 100) / maxTokens) : 0,
      title: `${d.date}: ${formatTokens(tokens)} tokens`,
    }
  })
  return { bars, maxTokens }
}

/**
 * The Free / Pro / Max cards: each plan's monthly allowance, the current one marked, and what its
 * button does — `upgrade` (the existing Pro checkout overlay) only for a Free user's Pro card;
 * Max is not sold yet.
 */
export function planCards(plans, currentPlan) {
  const current = normalizePlan(currentPlan)
  const byPlan = Object.fromEntries((Array.isArray(plans) ? plans : []).map((p) => [normalizePlan(p?.plan), p]))
  return PLAN_ORDER.map((plan) => {
    const p = byPlan[plan] || {}
    const quotaTokens = num(p.quota_tokens) || DEFAULT_PLAN_QUOTAS[plan]
    let action = null
    if (plan === current) action = 'current'
    else if (plan === 'pro' && current === 'free') action = 'upgrade'
    else if (plan === 'max') action = 'soon'
    return {
      plan,
      label: PLAN_LABELS[plan],
      quotaTokens,
      quotaLabel: `${formatTokens(quotaTokens)} tokens / month`,
      costCapUsd: p.cost_cap_usd ?? null,
      current: plan === current,
      action,
    }
  })
}

/** Is this axios error busy-intelligent-srv's quota refusal (as relayed by busy-main-bff)? */
export function isQuotaExceeded(error) {
  const res = error?.response
  if (!res || res.status !== 429) return false
  const d = res.data || {}
  return d.code === 'quota_exceeded' || d.UserMessageCode === 'quota_exceeded'
}

/** The sentence shown when a request is refused for the quota. */
export function quotaExceededMessage(payload) {
  const d = payload || {}
  const plan = normalizePlan(d.plan)
  const resets = formatResetDate(d.resets_at)
  const when = resets ? ` It resets on ${resets}.` : ''
  const upgrade = plan === 'free' ? ' Upgrade to Pro for a larger allowance.' : ''
  return `You've used this month's AI allowance on the ${PLAN_LABELS[plan]} plan.${when}${upgrade}`
}

// Several AI calls can be refused at once (a story page asks for follow-ups and connections
// together); one message per few seconds is enough.
const ANNOUNCE_GAP_MS = 10000
let lastAnnouncedAt = -Infinity

/**
 * The interceptor's hook: when `error` is a quota refusal, say so once (`notify`) and, for a Free
 * user, open the existing Pro upgrade overlay (`upgrade`). Returns whether it was one.
 */
export function announceQuotaExceeded(error, { notify, upgrade, now = Date.now() } = {}) {
  if (!isQuotaExceeded(error)) return false
  if (now - lastAnnouncedAt < ANNOUNCE_GAP_MS) return true
  lastAnnouncedAt = now
  const data = error.response.data || {}
  if (notify) notify(quotaExceededMessage(data))
  if (upgrade && normalizePlan(data.plan) === 'free') upgrade()
  return true
}

/** Tests only: forget the last announcement. */
export const resetQuotaAnnouncements = () => { lastAnnouncedAt = -Infinity }
